import { parse, Kind, valueFromASTUntyped } from 'graphql';

// This is a dashboard guard, not server authorization. Unknown requests fail closed.
const fields = (...names) => Object.fromEntries(names.map((name) => [name, true]));
const app = fields('id', 'platform', 'remoteAppId', 'version', 'token', 'buildInProgress');
const template = fields('id', 'name', 'tag', 'version', 'legacyId');
const extension = fields('id', 'handle', 'name', 'settingsSchema', 'backendSchema',
    'createdAt', 'updatedAt', 'description', 'packageVersion', 'icon', 'type');
const theme = fields('id', 'handle', 'name', 'packageName', 'packageVersion',
    'backendSchema', 'config', 'createdAt', 'description', 'icon', 'legacyId',
    'permissions', 'settingsSchema', 'status', 'type');
const installation = fields('id', 'status', 'settings', 'backendSettings',
    'androidVersion', 'iosVersion', 'createdAt', 'updatedAt');
const extensionInstall = { ...installation, extension };
const themeInstall = {
    ...installation,
    isDefault: true,
    project: fields('remoteProjectId', 'id', 'name'),
    theme,
};
const build = {
    ...fields('id', 'name', 'platform', 'version', 'buildType', 'triggerType',
        'createdAt', 'updatedAt', 'downloadUrl', 'qaStatus', 'releaseStatus', 'packages',
        'buildOptions', 'buildProgress', 'errors', 'uploadStatus', 'buildStatus',
        'legacyId', 'config'),
    appId: app,
    project: fields('remoteProjectId'),
    triggerBy: fields('name'),
    templateVersion: template,
};
const queryFields = {
    project: {
        templateTag: true,
        apps: app,
        meta: fields('hideLegacyDownload'),
    },
    templateVersions: template,
    apps: app,
    appBuildsCount: true,
    appBuilds: build,
    appBuild: build,
    appBuildErrors: { ...fields('id', 'errors'), appBuild: fields('id') },
    extensionsCount: true,
    extensions: extension,
    extension,
    extensionInstalls: extensionInstall,
    themes: theme,
    theme,
    themeInstalls: themeInstall,
    appBuildHistory: {
        pageInfo: fields('hasNextPage', 'hasPreviousPage', 'total'),
        nodes: {
            ...fields('id', 'createdAt', 'endedAt', 'buildName', 'templateTag',
                'triggerType', 'triggeredBy', 'buildType', 'appId', 'buildFile',
                'buildConfig', 'version', 'appmakerSdkName', 'appmakerSdkVersion'),
            platform: fields('type', 'status'),
        },
    },
};
const mutationFields = {
    createAppBuild: fields('id', 'appId'),
    createAppBuilds: { id: true, appId: app },
    appBuild: fields('id'),
    appBuildRetry: fields('id'),
    uploadIpa: true,
    updateAppBuild: fields('id'),
    deleteAppBuild: fields('id'),
    createExtensionInstall: fields('id'),
    updateExtensionInstall: fields('id', 'settings'),
    deleteExtensionInstall: fields('id'),
    importExtension: {
        id: true,
        project: fields('remoteProjectId'),
        extension: fields('id', 'name', 'handle'),
    },
    createThemeInstall: themeInstall,
    updateThemeInstall: fields('id'),
    deleteThemeInstall: fields('id'),
    importTheme: {
        id: true,
        project: fields('remoteProjectId'),
        theme: fields('id', 'name', 'handle'),
    },
};

const buildCreateKeys = ['name', 'buildType', 'triggerType', 'appId', 'platform',
    'packages', 'project', 'triggerBy', 'templateVersion', 'skipDefaultPackages', 'buildOptions'];
const installUpdateKeys = ['status', 'settings', 'backendSettings', 'androidVersion', 'iosVersion'];
const mutationDataKeys = {
    createAppBuild: buildCreateKeys,
    createAppBuilds: buildCreateKeys,
    updateAppBuild: ['qaStatus', 'releaseStatus'],
    createExtensionInstall: ['status', 'extension', 'project'],
    updateExtensionInstall: installUpdateKeys,
    createThemeInstall: ['theme', 'project'],
    updateThemeInstall: [...installUpdateKeys, 'isDefault'],
};

export class BuildOnlyApiError extends Error {
    constructor() {
        super('This request is unavailable. This dashboard only supports App Builds, Extensions, and Themes.');
        this.name = 'BuildOnlyApiError';
        this.code = 'APP_BUILDS_ONLY';
    }
}
const deny = () => { throw new BuildOnlyApiError(); };

function checkMutationData(selection, variables) {
    const name = selection.name.value;
    const dataArgument = selection.arguments.find((argument) => argument.name.value === 'data');
    if (!dataArgument) return;
    const data = valueFromASTUntyped(dataArgument.value, variables);
    const allowedKeys = mutationDataKeys[name];
    if (!allowedKeys) deny();
    const items = Array.isArray(data) ? data : [data];
    items.forEach((item) => {
        if (!item || typeof item !== 'object' || Object.keys(item).some((key) => !allowedKeys.includes(key))) deny();
        // Builds and installs may reference existing records, but cannot edit them.
        ['project', 'appId', 'triggerBy', 'templateVersion', 'extension', 'theme'].forEach((key) => {
            if (item[key] && (typeof item[key] !== 'object' ||
                Object.keys(item[key]).some((action) => action !== 'connect'))) deny();
        });
    });
}

export function assertBuildGraphQL(payload) {
    if (!payload || Array.isArray(payload) || typeof payload.query !== 'string') deny();
    let document;
    try { document = parse(payload.query); } catch (_) { deny(); }
    const fragments = new Map();
    const operations = [];
    document.definitions.forEach((definition) => {
        if (definition.kind === Kind.FRAGMENT_DEFINITION) {
            if (fragments.has(definition.name.value)) deny();
            fragments.set(definition.name.value, definition);
        } else if (definition.kind === Kind.OPERATION_DEFINITION) {
            operations.push(definition);
        } else deny();
    });
    if (!operations.length) deny();

    function checkSelection(selectionSet, allowed, ancestors = [], variables = {}) {
        if (!selectionSet || allowed === true) deny();
        selectionSet.selections.forEach((selection) => {
            if (selection.kind === Kind.FIELD) {
                const name = selection.name.value; // Ignore aliases when checking access.
                const child = name === '__typename' ? true :
                    (Object.prototype.hasOwnProperty.call(allowed, name) ? allowed[name] : undefined);
                if (!child) deny();
                if (allowed === mutationFields) checkMutationData(selection, variables);
                if (selection.selectionSet) checkSelection(selection.selectionSet, child, ancestors);
                else if (child !== true) deny();
            } else if (selection.kind === Kind.INLINE_FRAGMENT) {
                checkSelection(selection.selectionSet, allowed, ancestors, variables);
            } else if (selection.kind === Kind.FRAGMENT_SPREAD) {
                const name = selection.name.value;
                if (ancestors.includes(name) || !fragments.has(name)) deny();
                checkSelection(fragments.get(name).selectionSet, allowed, [...ancestors, name], variables);
            } else deny();
        });
    }
    // Validate every operation, including mixed documents and fragment selections.
    operations.forEach((operation) => {
        const allowed = operation.operation === 'query' ? queryFields :
            operation.operation === 'mutation' ? mutationFields : null;
        if (!allowed) deny();
        const variables = {};
        operation.variableDefinitions.forEach((definition) => {
            if (definition.defaultValue) variables[definition.variable.name.value] = valueFromASTUntyped(definition.defaultValue);
        });
        checkSelection(operation.selectionSet, allowed, [], { ...variables, ...payload.variables });
    });
}

export function assertBuildApiRequest({ url, baseURL, method = 'GET', data }) {
    let path;
    try {
        const combined = baseURL && !/^([a-z][a-z\d+.-]*:)?\/\//i.test(url) ?
            `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : url;
        const parsed = new URL(combined, window.location.origin);
        if (!['http:', 'https:'].includes(parsed.protocol)) deny();
        path = parsed.pathname.replace(/\/$/, '');
    } catch (_) { deny(); }
    const verb = method.toUpperCase();
    if (verb === 'POST' && (/^\/v3\/appmaker-gql$/.test(path) ||
        /^\/dashboard\/graphql\/v1\/\d+$/.test(path))) {
        let payload = data;
        if (typeof payload === 'string') {
            try { payload = JSON.parse(payload); } catch (_) { deny(); }
        }
        assertBuildGraphQL(payload);
        return;
    }
    if (verb === 'GET' && (
        /^\/v2\/user(?:\/logout|\/sso-login|\/shopify\/login)?$/.test(path) ||
        /^\/v2\/projects\/(?:list-project|\d+)$/.test(path) ||
        /^\/v[23]\/download-app\/\d+$/.test(path)
    )) return;
    deny();
}

export function installBuildOnlyAxiosGuard(client) {
    const adapter = client.defaults.adapter;
    // Defaults also propagate to axios.create() clients; validation runs before I/O.
    client.defaults.adapter = async (config) => {
        assertBuildApiRequest(config);
        return adapter(config);
    };
}

export function createBuildOnlyFetch(fetcher) {
    return async (url, options = {}) => {
        assertBuildApiRequest({ url, method: options.method, data: options.body });
        return fetcher(url, options);
    };
}
